import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { MagnifyingGlass, MapPin, Buildings, UserCirclePlus, House, SignOut } from "@phosphor-icons/react";
import { pickField } from "../lib/i18nField";
import { countryLabel, serviceTagLabel } from "../lib/labels_ar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useAuthAxios() {
  const token = localStorage.getItem("esg_token");
  return useMemo(() => axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);
}

export default function AdvisorsGallery() {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const http = useAuthAxios();

  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ countries: [], cities: {}, tags: [] });
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [myProfile, setMyProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const dir = lang === "ar" ? "rtl" : "ltr";

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (country) params.country = country;
      if (city) params.city = city;
      if (tag) params.tag = tag;
      if (q) params.q = q;
      const r = await http.get("/advisors", { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load professionals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    http.get("/advisors/filters/options").then(r => setFilters(r.data)).catch(() => {});
    http.get("/advisors/me").then(r => setMyProfile(r.data.profile)).catch(() => {});
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-line */ }, [country, tag]);

  const onSearch = (e) => { e.preventDefault(); load(); };
  const clearFilters = () => { setCountry(""); setCity(""); setTag(""); setQ(""); setTimeout(load, 0); };

  const ctaLabel = myProfile ? t("advisors_edit_my") : t("advisors_generate_mine");
  const ctaRoute = myProfile ? "/professionals/edit" : "/professionals/create";

  return (
    <div dir={dir} className="min-h-screen bg-[#070709] text-white" data-testid="advisors-gallery">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070709] border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2" data-testid="nav-logo-home">
            <Logo size="md" />
          </button>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard"
              className="hidden sm:flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              <House size={16} weight="regular" /> {t("dashboard")}
            </button>
            <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="nav-logout"
              className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
              <SignOut size={16} /> {t("logout")}
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-32 sm:pt-36 lg:pt-40 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#01AF9B]/80 mb-3">{t("advisors_eyebrow")}</p>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight">{t("advisors_title")}</h1>
            <p className="text-white/60 mt-3 max-w-2xl">{t("advisors_subtitle")}</p>
          </div>
          <button
            onClick={() => navigate(ctaRoute)}
            data-testid="advisors-generate-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F97316] hover:bg-[#EA630C] text-white font-semibold text-sm transition-colors shadow-[0_6px_20px_rgba(249,115,22,0.35)]">
            <UserCirclePlus size={18} weight="bold" />
            {ctaLabel}
          </button>
        </div>

        {/* Filters */}
        <form onSubmit={onSearch} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 sm:p-5 mb-8 grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-white/60" />
            <input
              type="text" value={q} onChange={e => setQ(e.target.value)}
              placeholder={t("advisors_search_placeholder")} data-testid="advisors-search-input"
              className="w-full ps-9 pe-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#01AF9B] focus:bg-white/[0.14] transition-colors"
            />
          </div>
          <select value={country} onChange={e => setCountry(e.target.value)}
            data-testid="advisors-country-filter"
            className="px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-sm text-white focus:outline-none focus:border-[#01AF9B] focus:bg-white/[0.14] cursor-pointer transition-colors">
            <option value="">{t("advisors_all_countries")}</option>
            {(filters.countries || []).map(c => <option key={c} value={c} className="bg-[#111216]">{countryLabel(c, lang)}</option>)}
          </select>
          <select value={tag} onChange={e => setTag(e.target.value)}
            data-testid="advisors-tag-filter"
            className="px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-sm text-white focus:outline-none focus:border-[#01AF9B] focus:bg-white/[0.14] cursor-pointer transition-colors">
            <option value="">{t("advisors_all_services")}</option>
            {(filters.tags || []).map(c => <option key={c} value={c} className="bg-[#111216]">{serviceTagLabel(c, lang)}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="submit" data-testid="advisors-search-submit"
              className="px-4 py-2 rounded-lg bg-[#01AF9B] hover:bg-[#018F7E] text-white font-medium text-sm transition-colors">
              {t("advisors_search_btn")}
            </button>
            <button type="button" onClick={clearFilters} data-testid="advisors-clear-filters"
              className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/70 transition-colors">
              {t("advisors_clear")}
            </button>
          </div>
        </form>

        {/* Gallery */}
        {loading ? (
          <div className="text-center py-20 text-white/50" data-testid="advisors-loading">{t("loading")}…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="advisors-empty">
            <p className="text-white/60 mb-3">{t("advisors_empty_title")}</p>
            <p className="text-white/40 text-sm">{t("advisors_empty_hint")}</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="advisors-grid">
            {items.map(ad => (
              <button key={ad.id}
                onClick={() => navigate(`/professionals/${ad.slug}`)}
                data-testid={`advisor-card-${ad.slug}`}
                className="group text-start bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-[#01AF9B]/30 transition-colors">
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white/10 group-hover:border-[#01AF9B]/50 mb-4 bg-white/5">
                    {ad.image_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${ad.image_url}`}
                           alt={ad.company_name}
                           className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">{ad.company_name?.[0] || "?"}</div>
                    )}
                  </div>
                  <h3 className="text-base font-semibold mb-1 line-clamp-1" dir="auto">{pickField(ad, "company_name", lang)}</h3>
                  <p className="text-xs text-white/50 mb-3">{ad.first_name} {ad.last_name}</p>
                  <p className="text-sm text-white/70 line-clamp-3 mb-4" dir="auto">{pickField(ad, "hero_message", lang)}</p>
                  <div className="flex items-center gap-2 text-xs text-[#01AF9B]/80 mb-3">
                    <MapPin size={12} /> <span dir="auto">{pickField(ad, "city", lang)}, {countryLabel(ad.country, lang)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {(ad.service_tags || []).slice(0, 3).map(t => (
                      <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">{serviceTagLabel(t, lang)}</span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
